import { computed, Injectable, signal } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import { GuestDTO, SpaDiscountDTO, SpaSimpleProductDTO } from '@api';
import { GUTHABEN_DISCOUNT_REASON } from '@utils';

@Injectable({
  providedIn: 'root',
})
export class BookingCompanionsService {
  private companionIds = new BehaviorSubject<string[] | null>([]);
  currentCompanionIds = this.companionIds.asObservable();

  numberOfCompanions = new BehaviorSubject<number | null>(0);
  currentNumberOfCompanions = this.numberOfCompanions.asObservable();

  private productID = new BehaviorSubject<string | null>('');
  currentProductID = this.productID.asObservable();

  companionsMap = new BehaviorSubject<Record<string, GuestDTO[]>>({});
  currentCompanionMap = this.companionsMap.asObservable();

  setCompanionsMap(selectedCompanionsMap: Record<string, GuestDTO[]>): void {
    this.companionsMap.next(selectedCompanionsMap);
  }

  setCompanions(ids: string[]): void {
    this.companionIds.next(ids);
  }
  setNumberOfCompanions(numberOfCompanions: number): void {
    this.numberOfCompanions.next(numberOfCompanions);
  }

  setProductId(productID: string): void {
    this.productID.next(productID);
  }

  // TODO rename when other Subjects are migrated or removed (AIDA-1101)
  s_selectedProduct = signal<SpaSimpleProductDTO | undefined>(undefined);
  s_selectedMainGuests = signal<GuestDTO[]>([]);
  s_discountsMap = signal<DiscountMapType>({});
  s_companionMap = signal<CompanionMapType>({});
  s_productPrice = computed(() => this.s_selectedProduct()?.price);
  s_subTotalPriceMap = computed(() => this.calculateSubTotalPrices());
  s_subTotalNetPriceMap = computed(() => this.calculateSubTotalNetPrices());
  // for completeness
  s_totalPrice = computed(() => Object.values(this.s_subTotalPriceMap()).reduce((acc, price) => acc + price, 0));
  s_totalNetPrice = computed(() => Object.values(this.s_subTotalNetPriceMap()).reduce((acc, price) => acc + price, 0));

  private calculateSubTotalPrices() {
    const productPrice = this.getSelectedProductPrice() ?? 0;

    const subTotalPriceMap: PriceMapType = {};
    for (const index of this.s_selectedMainGuests().keys()) {
      let subTotalPrice = productPrice;
      const companionPrice = this.getCompanionsPriceForGuest(index);
      subTotalPrice += companionPrice ?? 0;
      subTotalPriceMap[index] = subTotalPrice;
    }

    return subTotalPriceMap;
  }

  private calculateSubTotalNetPrices() {
    const subTotalNetPriceMap: PriceMapType = {};

    for (const index of this.s_selectedMainGuests().keys()) {
      const subTotalPrice = this.s_subTotalPriceMap()[index];
      const discountDTO = this.s_discountsMap()[index];

      if (!discountDTO) {
        subTotalNetPriceMap[index] = subTotalPrice;
        continue;
      }

      let subTotalNetPrice: number;
      if (discountDTO.unit === 'absolute') {
        subTotalNetPrice = subTotalPrice - discountDTO.amount;
      } else {
        subTotalNetPrice = subTotalPrice - (subTotalPrice * discountDTO.amount) / 100;
      }
      const isGuthabenDiscountReason = discountDTO.reason === GUTHABEN_DISCOUNT_REASON;
      if (!isGuthabenDiscountReason && subTotalNetPrice < subTotalPrice / 2) {
        subTotalNetPrice = subTotalPrice / 2;
      }
      subTotalNetPriceMap[index] = subTotalNetPrice;
    }

    return subTotalNetPriceMap;
  }

  getSelectedProductPrice() {
    return this.s_selectedProduct()?.price;
  }

  private getSelectedProductCompanionPrice(companionIndex: number) {
    return this.s_selectedProduct()?.companions[companionIndex].price;
  }

  private getCompanionsPriceForGuest(guestIndex: number) {
    const companionsMap = this.s_companionMap();
    const mainGuestCompanions = Object.keys(companionsMap).filter((key) => key.split('-')[0] === guestIndex.toString());
    let totalCompanionPrice = 0;
    for (const key of mainGuestCompanions) {
      const companionIndex = Number.parseInt(key.split('-')[1]);
      if (companionsMap[key]) {
        totalCompanionPrice += this.getSelectedProductCompanionPrice(companionIndex) ?? 0;
      }
    }
    return totalCompanionPrice;
  }
}

export type DiscountMapType = Record<string, SpaDiscountDTO>;
export type CompanionMapType = Record<string, GuestDTO>;
export type PriceMapType = Record<string, number>;
